/**
 * ChatSheet, full-screen RAG conversation.
 *
 * Slides up over the landing when the visitor taps the ask card or a
 * suggestion chip. Uses the existing POST /api/rag/ask endpoint (already
 * shipped, uses Claude Haiku 4.5 with tenant building knowledge as context).
 *
 * This component owns its own message list so multi-turn conversations work
 * in-session. It does NOT persist across sheet opens, each open starts fresh
 * with the localised welcome message.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowUp, RotateCw, Wrench, Mic, Square } from 'lucide-react';
import { cn } from '@tendedloop/ui';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';
import { transcribeAudio } from '@/services/voiceService';
import { isVoiceSupportedInBrowser, isLocaleSupportedForVoice } from '@/utils/voiceSupport';
import { apiErrorText, isImeComposing } from '@tendedloop/shared/i18n';

function normalizedEditDistance(a: string, b: string): number {
  if (!a && !b) return 0;
  // Lightweight Levenshtein for telemetry only. We only ever compare short
  // visitor utterances (≤ 300 chars), so the O(m·n) cost is negligible.
  const m = a.length;
  const n = b.length;
  if (m === 0) return 1;
  if (n === 0) return 1;
  const dp: number[] = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0]!;
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j]!;
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j]!, dp[j - 1]!);
      prev = tmp;
    }
  }
  return dp[n]! / Math.max(m, n);
}

interface ChatSheetProps {
  open: boolean;
  qrCode: string;
  apiUrl: string;
  /** Building name shown in the chat header (e.g. "TendedLoop · Atrium"). */
  buildingName: string;
  /** Seed question to fire as soon as the sheet opens (from a chip tap). */
  seed?: string | null;
  /** Visitor's remembered parking anchor amenityId (same building only).
   *  When set, the /ask endpoint uses it to answer "where did I park?" with
   *  walking directions instead of claiming it can't help. */
  anchorAmenityId?: string | null;
  onClose: () => void;
  /** Optional, close the chat and open the issue sheet as a follow-up. When
   *  passed, we render a "something's off?" chip under the latest AI answer
   *  so the visitor has a clear next step without hunting the landing page. */
  onReportIssue?: () => void;
}

type Role = 'system' | 'you' | 'ai' | 'typing' | 'error';
interface ChatMessage {
  id: string;
  role: Role;
  text: string;
  statusUpdated?: boolean;
  /** For error bubbles: the question that failed, so the retry chip can
   *  re-fire it without the visitor retyping. */
  retryQuestion?: string;
  /**
   * Outdoor maps deep-link target — when present, the bubble renders a
   * "Open in Maps" button under the answer. Universal Google Maps URL
   * scheme; no API key needed. Emitted by RAG when an outdoor destination
   * with a KB-verified address is referenced.
   */
  mapsDestination?: string;
  /**
   * Floor-plan URL surfaced by the LLM when the visitor asks for a map and
   * the KB contains a floor-plan link. Renders a "View floor plan" button
   * that opens the URL in a new tab.
   */
  floorPlanUrl?: string;
  floorPlanLabel?: string;
  /**
   * Google Calendar event suggestion. Emitted by the LLM when the visitor
   * asked to set a reminder / future event. Composes a universal
   * calendar.google.com/calendar/render URL — no API key, no auth.
   */
  calendarEvent?: {
    title: string;
    startIso: string;
    endIso: string;
    location?: string;
    details?: string;
  };
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Build a universal Google Calendar render URL. No API key, no auth.
 * Calendar expects timestamps in the compact `YYYYMMDDTHHMMSSZ` form, not
 * RFC 3339 — strip dashes / colons / fractional seconds.
 */
function buildCalendarUrl(ev: {
  title: string;
  startIso: string;
  endIso: string;
  location?: string;
  details?: string;
}): string {
  const toCalTs = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: `${toCalTs(ev.startIso)}/${toCalTs(ev.endIso)}`,
  });
  if (ev.location) params.set('location', ev.location);
  if (ev.details) params.set('details', ev.details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function ChatSheet({
  open,
  qrCode,
  apiUrl,
  buildingName,
  seed,
  anchorAmenityId,
  onClose,
  onReportIssue,
}: ChatSheetProps) {
  const { t, i18n } = useTranslation('visitor');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const firedSeedRef = useRef<string | null>(null);

  // Voice-input state. We track the last-transcribed text against the input
  // at send time so the edit-rate telemetry reflects whether visitors trust
  // the STT output or routinely re-write it. `isTranscribing` lives here
  // (not in the hook) because it's the network call's lifecycle, not the
  // microphone's — see useVoiceRecording.ts header.
  const [isTranscribing, setIsTranscribing] = useState(false);
  const lastTranscriptRef = useRef<string | null>(null);
  const voiceStartedAtRef = useRef<number>(0);

  const voiceSupported = isVoiceSupportedInBrowser();
  const voiceLocaleOk = isLocaleSupportedForVoice(i18n.language);
  const voiceAvailable = voiceSupported && voiceLocaleOk;

  // Ref-indirection so the hook's onAutoStop callback can call the latest
  // auto-submit logic without having to recreate the hook on every render.
  // The actual handler is wired up via useEffect after submitInput exists.
  const autoSubmitRef = useRef<((reason: 'silence' | 'cap', durationMs: number, blob: Blob | null) => void) | null>(
    null,
  );

  const voice = useVoiceRecording({
    onAutoStop: (reason, durationMs, blob) => {
      try {
        window.gtag?.('event', 'voice_recording_stopped', {
          stop_reason: reason,
          duration_ms: durationMs,
        });
      } catch {
        /* analytics is best-effort */
      }
      autoSubmitRef.current?.(reason, durationMs, blob);
    },
  });

  // Reset + welcome on open
  useEffect(() => {
    if (!open) return;
    setMessages([
      {
        id: uid(),
        role: 'system',
        text: t(
          'v2.chat.welcome',
          "Hey. Ask me anything about this place, opening hours, where things are, what's up.",
        ),
      },
    ]);
    setInput('');
    firedSeedRef.current = null;
    const t1 = setTimeout(() => {
      try {
        inputRef.current?.focus({ preventScroll: true });
      } catch {
        inputRef.current?.focus();
      }
    }, 350);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fire the seed question once per open. We render the question bubble
  // immediately so the chat never looks blank during the sheet transition ,
  // the visitor sees "their" question as soon as they tap the chip.
  useEffect(() => {
    if (!open || !seed || firedSeedRef.current === seed) return;
    firedSeedRef.current = seed;
    void ask(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages]);

  // Escape closes. Mid-recording, Escape cancels the recording instead so
  // the visitor's first instinct (Escape = abort) doesn't release the mic
  // AND close the whole sheet in one keystroke.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (voice.state === 'recording') {
        voice.cancel();
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose, voice]);

  // Fire voice_button_shown once per chat open when the affordance is
  // actually visible to the visitor, so we can attribute downstream
  // engagement back to the moment of exposure.
  useEffect(() => {
    if (!open || !voiceAvailable) return;
    try {
      window.gtag?.('event', 'voice_button_shown', { locale: i18n.language });
    } catch {
      /* best effort */
    }
  }, [open, voiceAvailable, i18n.language]);

  // Surface voice errors as an inline chat bubble so the visitor isn't
  // stuck in the silent-failure loop of (tap mic → dialog → Continue →
  // nothing visible → tap mic again). De-duped by errorCode so consecutive
  // taps don't pile up identical messages — `lastVoiceErrorRef` resets on
  // any non-error state, so a fresh failure after a success does show.
  const lastVoiceErrorRef = useRef<typeof voice.errorCode>(null);

  // Permissions API watcher: if a visitor unblocks the mic in another tab
  // or Chrome's site-settings panel, the error bubble auto-clears so they
  // can tap mic again without reloading. Standard on Android Chrome and
  // recent Safari; silently no-ops elsewhere.
  useEffect(() => {
    if (!open) return;
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    let cleanup: (() => void) | undefined;
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        const onChange = () => {
          if (status.state === 'granted' || status.state === 'prompt') {
            // Drop the error bubble + reset the de-dupe ref so the next
            // mic tap starts fresh. Don't auto-retry — surprise recordings
            // are worse than one extra tap.
            lastVoiceErrorRef.current = null;
            setMessages((m) => m.filter((x) => x.role !== 'error'));
            try {
              window.gtag?.('event', 'voice_permission_recovered', { state: status.state });
            } catch {
              /* best effort */
            }
          }
        };
        status.addEventListener('change', onChange);
        cleanup = () => status.removeEventListener('change', onChange);
      })
      .catch(() => {
        /* Permissions API not supported — graceful no-op */
      });
    return () => cleanup?.();
  }, [open]);
  useEffect(() => {
    if (voice.state === 'error' && voice.errorCode) {
      if (lastVoiceErrorRef.current === voice.errorCode) return;
      lastVoiceErrorRef.current = voice.errorCode;
      const msg =
        voice.errorCode === 'permission_denied'
          ? t(
              'v2.voice.permissionDenied',
              'Microphone permission denied. Allow it in browser settings, or type instead.',
            )
          : voice.errorCode === 'not_supported'
            ? t('v2.voice.notSupported', 'Voice input not supported on this device')
            : t('v2.voice.transcriptionError', "Couldn't hear that. Try again or type.");
      setMessages((m) => [...m, { id: uid(), role: 'error', text: msg }]);
      try {
        window.gtag?.('event', 'voice_error_shown', { error_code: voice.errorCode });
      } catch {
        /* best effort */
      }
    } else if (voice.state === 'idle' || voice.state === 'recording') {
      lastVoiceErrorRef.current = null;
    }
  }, [voice.state, voice.errorCode, t]);

  async function ask(question: string) {
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setMessages((m) => [...m, { id: uid(), role: 'you', text: q }, { id: 'typing', role: 'typing', text: '' }]);
    try {
      const res = await fetch(`${apiUrl}/api/rag/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qrCode,
          question: q,
          ...(anchorAmenityId ? { anchorAmenityId } : {}),
          // Visitor's reading locale so the LLM answers in this language
          // even when the tenant's building knowledge is written in another.
          language: i18n.language,
        }),
      });
      const json = await res.json();
      setMessages((m) => m.filter((x) => x.role !== 'typing'));
      if (!res.ok || !json.success || !json.data?.answer) {
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: 'error',
            text: apiErrorText(i18n, json, t('v2.chat.errorFallback', "I don't have that yet. I'll route it to ops.")),
            retryQuestion: q,
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: 'ai',
            text: json.data.answer,
            statusUpdated: !!json.data.statusUpdated,
            mapsDestination:
              typeof json.data.mapsAction?.destination === 'string' ? json.data.mapsAction.destination : undefined,
            floorPlanUrl:
              typeof json.data.floorPlanAction?.url === 'string' ? json.data.floorPlanAction.url : undefined,
            floorPlanLabel:
              typeof json.data.floorPlanAction?.label === 'string' ? json.data.floorPlanAction.label : undefined,
            calendarEvent: json.data.calendarAction
              ? {
                  title: String(json.data.calendarAction.title || ''),
                  startIso: String(json.data.calendarAction.startIso || ''),
                  endIso: String(json.data.calendarAction.endIso || ''),
                  location: json.data.calendarAction.location ? String(json.data.calendarAction.location) : undefined,
                  details: json.data.calendarAction.details ? String(json.data.calendarAction.details) : undefined,
                }
              : undefined,
          },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m.filter((x) => x.role !== 'typing'),
        {
          id: uid(),
          role: 'error',
          text: t('v2.chat.networkError', "Couldn't reach the building. Try again?"),
          retryQuestion: q,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  // Wrap the send path so both the Enter handler and the composer button
  // route through one place, and so we can fire voice telemetry consistently.
  const submitInput = useCallback(
    (raw: string) => {
      const q = raw.trim();
      if (!q || loading) return;
      const original = lastTranscriptRef.current;
      if (original !== null) {
        try {
          window.gtag?.('event', 'voice_transcript_submitted', { edited: original !== q });
          if (original !== q) {
            window.gtag?.('event', 'voice_transcript_edited', {
              edit_distance_normalized: normalizedEditDistance(original, q),
            });
          }
        } catch {
          /* best effort */
        }
        lastTranscriptRef.current = null;
      }
      setInput('');
      void ask(q);
    },
    // ask is defined above; ESLint can't infer its identity but the dependency
    // is intentional — re-creating submitInput when ask changes is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading],
  );

  // Shared transcribe → apply pipeline. autoSubmit=true means we send the
  // transcript straight through to RAG without showing it for review (the
  // zero-friction "stopped talking → submitted" path from silence/cap
  // auto-stop). autoSubmit=false means we fill the input and let the
  // visitor edit before sending (the manual-tap path).
  const transcribeAndApply = useCallback(
    async (blob: Blob, autoSubmit: boolean) => {
      setIsTranscribing(true);
      const t0 = Date.now();
      const result = await transcribeAudio({ blob, qrCode, language: i18n.language, apiUrl });
      setIsTranscribing(false);
      const latency = Date.now() - t0;
      if (result.ok) {
        const text = result.text.trim();
        if (!text) return; // empty transcript — nothing to do, don't pollute the chat
        lastTranscriptRef.current = text;
        try {
          window.gtag?.('event', 'voice_transcription_succeeded', {
            latency_ms: latency,
            language_detected: result.language,
            transcript_length: text.length,
            auto_submit: autoSubmit,
          });
        } catch {
          /* best effort */
        }
        if (autoSubmit) {
          // Skip filling the input — submitInput consumes the raw text
          // and pushes the "you" bubble + fires /api/rag/ask in one step.
          try {
            window.gtag?.('event', 'voice_auto_submitted', { source: 'silence_or_cap' });
          } catch {
            /* best effort */
          }
          submitInput(text);
        } else {
          setInput(text);
          setTimeout(() => {
            try {
              inputRef.current?.focus({ preventScroll: true });
            } catch {
              /* ignore */
            }
          }, 50);
        }
      } else {
        try {
          window.gtag?.('event', 'voice_transcription_failed', {
            error_code: result.code ?? 'unknown',
            latency_ms: latency,
          });
        } catch {
          /* best effort */
        }
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: 'error',
            text: apiErrorText(
              i18n,
              result,
              t('v2.voice.transcriptionError', "Couldn't hear that. Try again or type."),
            ),
          },
        ]);
      }
    },
    [submitInput, qrCode, i18n.language, apiUrl, t],
  );

  // Keep the auto-submit ref pointed at the latest transcribeAndApply so
  // the hook's onAutoStop callback (set once at construction) always picks
  // up the current closure over submitInput / qrCode / locale / apiUrl.
  useEffect(() => {
    autoSubmitRef.current = (_reason, _duration, blob) => {
      if (!blob) return;
      void transcribeAndApply(blob, true);
    };
  }, [transcribeAndApply]);

  const handleMicStart = useCallback(async () => {
    // Synchronously initiate getUserMedia inside the click handler. No
    // pre-flight consent dialog — Chrome's native permission prompt IS the
    // privacy gate. Adding our own sheet on top added a tap of friction with
    // no real benefit (the disclosure copy is in the page footer anyway).
    lastTranscriptRef.current = null;
    voiceStartedAtRef.current = Date.now();
    try {
      window.gtag?.('event', 'voice_recording_started', { locale: i18n.language });
    } catch {
      /* best effort */
    }
    await voice.start();
  }, [voice, i18n.language]);

  const handleMicStop = useCallback(async () => {
    const duration = Date.now() - voiceStartedAtRef.current;
    const blob = await voice.stop();
    // The hook fires onAutoStop with the correct reason for silence/cap;
    // here we cover the manual-tap path explicitly. Manual stop = visitor
    // wants to review, so fill the input rather than auto-submit.
    try {
      window.gtag?.('event', 'voice_recording_stopped', {
        stop_reason: 'tap',
        duration_ms: duration,
      });
    } catch {
      /* best effort */
    }
    if (!blob) return;
    await transcribeAndApply(blob, false);
  }, [voice, transcribeAndApply]);

  // Most-recent AI message id, used to render the "something still off?"
  // follow-up chip only under the latest AI answer, not after every turn.
  let lastAiId: string | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'ai') {
      lastAiId = messages[i]!.id;
      break;
    }
  }

  // Seed 3 example chips under the welcome bubble so the visitor has
  // something to tap in the void between header and composer. Taps send
  // the chip text as a question, same as typing it themselves.
  const seedExamples = (() => {
    const raw = t('v2.askCard.examples', { returnObjects: true, defaultValue: [] as string[] });
    const arr = Array.isArray(raw) ? (raw as string[]) : [];
    // Strip surrounding quote chars the locale uses for the rotating hint ,
    // chips read more naturally as plain questions.
    return arr
      .slice(0, 3)
      .map((s) => s.replace(/^["']|["']$/g, '').trim())
      .filter(Boolean);
  })();
  const showSeedChips = messages.length === 1 && messages[0]?.role === 'system' && seedExamples.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-sheet-title"
      aria-hidden={!open}
      className={cn(
        'fixed inset-0 z-50 flex flex-col bg-canvas',
        'transition-transform duration-300 ease-out',
        open ? 'translate-y-0 visible pointer-events-auto' : 'translate-y-full invisible pointer-events-none',
      )}
      style={{ background: '#FAFAF7' }}
    >
      {/* Header */}
      <header
        className="flex items-center gap-2.5 px-5 pb-3.5 pt-[max(1.25rem,env(safe-area-inset-top))]"
        style={{ borderBottom: '1px solid #EEF0F2' }}
      >
        <div
          aria-hidden="true"
          className="flex h-10 w-10 items-center justify-center rounded-full text-[16px] font-bold text-white"
          // Uses --tl-accent so the avatar reflects the operator's brand
          // color when they've opted in (white-label alignment). Default
          // is the TendedLoop teal.
          style={{ background: 'var(--tl-accent, #0a5958)' }}
        >
          T
        </div>
        <div className="min-w-0 flex-1">
          <div id="chat-sheet-title" className="truncate text-[17px] font-bold leading-tight tracking-[-0.01em]">
            {t('v2.chat.title', '{{building}}', { building: buildingName })}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[15px] font-medium text-muted-foreground">
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] rounded-full"
              style={{ background: '#10B981', animation: 'pulse 1.8s infinite' }}
            />
            {t('v2.chat.subtitle', 'answers right now')}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('v2.close', 'Close')}
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-all active:scale-95"
          style={{ background: '#F3F4F0', color: '#1F2833' }}
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      {/* Messages */}
      <div ref={bodyRef} className="flex flex-1 flex-col gap-3 overflow-y-auto px-[18px] pb-[100px] pt-5">
        {messages.map((m) =>
          m.role === 'you' ? (
            <div
              key={m.id}
              className="max-w-[85%] self-end rounded-[18px] rounded-ee-[4px] px-4 py-3 text-[16px] font-medium leading-relaxed text-white"
              style={{ background: '#0A0E14' }}
            >
              {m.text}
            </div>
          ) : m.role === 'typing' ? (
            <div
              key={m.id}
              className="inline-flex gap-1.5 self-start rounded-[18px] rounded-es-[4px] border border-slate-200 bg-white px-3.5 py-2.5"
              aria-live="polite"
            >
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
                style={{ animationDelay: '300ms' }}
              />
            </div>
          ) : m.role === 'error' ? (
            <div
              key={m.id}
              className="max-w-[90%] self-start rounded-[18px] rounded-es-[4px] border border-red-200 bg-red-50 px-4 py-3 text-[16px] font-medium leading-relaxed text-red-900"
            >
              {m.text}
              {m.retryQuestion && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => void ask(m.retryQuestion!)}
                    disabled={loading}
                    className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-red-300 bg-white px-3.5 py-3 text-[15px] font-semibold text-red-900 transition-transform active:scale-[0.97] disabled:opacity-50"
                  >
                    <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('v2.chat.retry', 'Try again')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              key={m.id}
              className={cn(
                'max-w-[90%] self-start rounded-[18px] rounded-es-[4px] border bg-white px-4 py-3 text-[16px] font-medium leading-relaxed text-foreground',
              )}
              style={{ borderColor: '#EEF0F2' }}
            >
              {m.text}
              {m.role === 'ai' && (
                <>
                  <div
                    className="mt-2.5 border-t pt-2.5 text-[15px] font-medium text-muted-foreground"
                    style={{ borderColor: '#EEF0F2' }}
                  >
                    {m.statusUpdated
                      ? t('v2.chat.citeStatusUpdated', 'logged · updated the amenity status')
                      : t('v2.chat.cite', 'from building knowledge · updated today')}
                  </div>
                  {m.floorPlanUrl && (
                    <div className="mt-3">
                      <a
                        href={m.floorPlanUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-[#E8EAED] bg-white px-3.5 py-3 text-[15px] font-semibold text-[#0A0E14] transition-transform active:scale-[0.97]"
                        data-testid="chat-floorplan-link"
                      >
                        🗺️ {m.floorPlanLabel || t('v3.chat.viewFloorPlan', 'View floor plan')}
                      </a>
                    </div>
                  )}
                  {m.mapsDestination && (
                    <div className="mt-3">
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                          m.mapsDestination,
                        )}&travelmode=walking`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full px-3.5 py-3 text-[15px] font-semibold text-white transition-transform active:scale-[0.97]"
                        style={{ background: '#1a73e8' }}
                        data-testid="chat-maps-deeplink"
                      >
                        📍 {t('v3.chat.openInMaps', 'Open in Maps')}
                      </a>
                    </div>
                  )}
                  {m.calendarEvent && (
                    <div className="mt-3">
                      <a
                        href={buildCalendarUrl(m.calendarEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-[#E8EAED] bg-white px-3.5 py-3 text-[15px] font-semibold text-[#0A0E14] transition-transform active:scale-[0.97]"
                        data-testid="chat-calendar-link"
                      >
                        🗓️ {t('v3.chat.addToCalendar', 'Add to Google Calendar')}
                      </a>
                    </div>
                  )}
                  {m.id === lastAiId && onReportIssue && !m.statusUpdated && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={onReportIssue}
                        className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-[#E8EAED] bg-white px-3.5 py-3 text-[15px] font-semibold text-[#0A0E14] transition-transform active:scale-[0.97]"
                      >
                        <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('v2.chat.reportInstead', "Something's still off?")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ),
        )}
        {showSeedChips && (
          <div className="mt-1 flex flex-wrap gap-2" role="list" aria-label={t('v2.chips.aria', 'Suggested questions')}>
            {seedExamples.map((ex) => (
              <button
                key={ex}
                type="button"
                role="listitem"
                onClick={() => void ask(ex)}
                disabled={loading}
                className="inline-flex min-h-[48px] items-center rounded-full border border-[#E8EAED] bg-white px-3.5 py-3 text-[15px] font-medium text-[#0A0E14] transition-transform active:scale-[0.97] disabled:opacity-50"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] start-4 end-4 flex items-center gap-2 rounded-full bg-white px-1.5 ps-[18px] py-1.5 focus-within:ring-2 focus-within:ring-ring"
        style={{ border: '1px solid #E2E6EA', boxShadow: '0 12px 32px -8px rgba(10,14,20,0.15)' }}
      >
        <input
          ref={inputRef}
          type="text"
          dir="auto"
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 300))}
          disabled={voice.state === 'recording' || isTranscribing}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading && !isImeComposing(e)) {
              e.preventDefault();
              submitInput(input);
            }
          }}
          placeholder={
            voice.state === 'recording'
              ? t('v2.voice.listening', 'Listening…')
              : isTranscribing
                ? t('v2.voice.transcribing', 'Transcribing…')
                : t('v2.chat.inputPh', 'Ask or describe…')
          }
          aria-label={t('v2.chat.inputPh', 'Ask or describe…')}
          className="flex-1 bg-transparent text-[16px] font-medium text-foreground outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
        />
        {voice.state === 'recording' ? (
          <button
            type="button"
            onClick={() => void handleMicStop()}
            aria-label={t('v2.voice.stop', 'Stop')}
            className="h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 motion-safe:animate-pulse"
            style={{ background: '#DC2626' }}
          >
            <Square className="h-3.5 w-3.5" aria-hidden="true" fill="currentColor" />
          </button>
        ) : voiceAvailable && !input.trim() && !isTranscribing ? (
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleMicStart()}
            aria-label={t('v2.voice.tapToSpeak', 'Tap to speak')}
            className="h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
            style={{ background: '#0A0E14', color: '#FAFAF7' }}
          >
            <Mic className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            disabled={loading || !input.trim() || isTranscribing}
            onClick={() => submitInput(input)}
            aria-label={t('v2.chat.send', 'Send')}
            className="h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
            style={{ background: '#0A0E14', color: '#FAFAF7' }}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* aria-live region for voice state announcements — visually hidden
          but consumed by screen readers so non-sighted visitors learn
          when listening starts and stops. */}
      <div className="sr-only" aria-live="polite">
        {voice.state === 'recording'
          ? t('v2.voice.listening', 'Listening…')
          : isTranscribing
            ? t('v2.voice.transcribing', 'Transcribing…')
            : ''}
      </div>
    </div>
  );
}
